import React, { useEffect, useMemo, useState } from 'react';

import { debounce } from 'lodash';
import {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  Path,
  FieldValues,
  PathValue,
} from 'react-hook-form';

import { ReactComponent as PencilSvg } from 'assets/svg/pencil.svg';
import { EMAIL_FIELD_VALIDATION, EMAIL_PATTERN } from 'constants/global';
import { ButtonVariants } from 'constants/shared/button';
import { Option } from 'interfaces';
import { getInvestors } from 'modules/investors/action';
import { selectInvestorsData } from 'modules/investors/selectors';
import { useAppDispatch, useAppSelector } from 'modules/store';
import { Button, CreatableSelectFormInput, FormContent, SnippetLayout } from 'shared-components';

interface Props<T extends FieldValues = FieldValues> {
  control: Control<T>;
  register: UseFormRegister<T>;
  setValue: UseFormSetValue<T>;
  errors: FieldErrors<T>;
  watch: UseFormWatch<T>;
  formFields: (emailOptions: Option[], setValue: UseFormSetValue<T>, investors: any[], isFormEditable: boolean) => any;
  fieldNames: {
    EMAIL: Path<T>;
    FIRST_NAME: Path<T>;
    LAST_NAME: Path<T>;
    INVESTOR_ID: Path<T>;
  };
  hiddenEditButton?: boolean;
  handleInvestorId?: (investorId: number | null) => void;
}

const ShareExchangeInvestorFields = <T extends FieldValues = FieldValues>({
  control,
  register,
  setValue,
  errors,
  watch,
  formFields,
  fieldNames,
  hiddenEditButton = false,
  handleInvestorId,
}: Props<T>) => {
  const [isFormEditable, setIsFormEditable] = useState(false);
  const dispatch = useAppDispatch();
  const investors = useAppSelector(selectInvestorsData)?.list || [];

  const watchEmail = watch(fieldNames.EMAIL);

  const onCreateOption = (inputValue: string) => {
    return EMAIL_PATTERN.test(inputValue);
  };

  const setAsUnavailable = () => setIsFormEditable(false);

  const handleInputChange = debounce((newValue: string) => {
    dispatch(
      getInvestors({
        page: 1,
        per_page: 20,
        query: newValue,
      }),
    );
  }, 1500);

  const emailOptions: Option[] = useMemo(() => {
    return (
      investors?.map(({ email, id, isEntity }) => ({
        label: isEntity ? `${email} (Entity)` : email,
        value: email,
        id: id.toString(),
      })) || []
    );
  }, [investors]);

  useEffect(() => {
    if (!watchEmail) return;
    const emailOption = watchEmail as unknown as Option;

    const selectedInvestor = investors?.find(({ id }) => id === Number(emailOption.id));
    setValue(fieldNames.FIRST_NAME, selectedInvestor?.firstName as PathValue<T, Path<T>>);
    setValue(fieldNames.LAST_NAME, selectedInvestor?.lastName as PathValue<T, Path<T>>);
    setValue(fieldNames.INVESTOR_ID, (selectedInvestor?.id ?? 0) as PathValue<T, Path<T>>);
    setIsFormEditable(!!(selectedInvestor?.firstName && selectedInvestor?.lastName));

    if (handleInvestorId) {
      handleInvestorId(selectedInvestor?.id ?? null);
    }
  }, [watchEmail]);

  return (
    <SnippetLayout title='Investor' childrenClassName='grid grid-cols-4 md:grid-cols-[1fr_1fr_1fr] gap-4'>
      <CreatableSelectFormInput
        required
        className='col-span-full md:col-span-1'
        label='Email'
        placeholder='Email'
        control={control as Control}
        validation={EMAIL_FIELD_VALIDATION}
        name={fieldNames.EMAIL as string}
        options={emailOptions}
        onInputChange={(newValue, actionMeta) => {
          if (actionMeta.action === 'input-change') {
            handleInputChange(newValue);
          }
        }}
        onCreateOption={onCreateOption}
      />
      <FormContent
        fields={formFields(emailOptions, setValue, investors, isFormEditable)}
        control={control}
        register={register}
        errors={errors}
      />
      {!hiddenEditButton && (
        <Button
          type='button'
          variant={ButtonVariants.SECONDARY}
          className='col-span-full sm:col-span-1 w-full sm:w-fit mb-6'
          onClick={setAsUnavailable}
        >
          Edit <PencilSvg className='ml-2' />
        </Button>
      )}
    </SnippetLayout>
  );
};

export default ShareExchangeInvestorFields;
